import * as React from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements,
  IbanElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useState, useEffect } from 'react';
import { StripeIbanInputContainer } from './styled';
import { useMutation } from '@apollo/client';
import {
  registerSourceForStoreMutation,
  registerSourceIntentMutation,
} from '../../graphql/mutations';
import { AppConfig } from '@/utils/config';
import { paymentMethodTypes } from '@/utils/paymentMethods';
import { AddPaymentModalProps } from './types';
import i18n from '@/utils/i18n';

const stripePromise = loadStripe(AppConfig.STRIPE_PUBLIC_KEY);

// Custom styling can be passed as options when creating an Element.
const IBAN_STYLE = {
  base: {
    color: '#32325d',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4',
    },
    ':-webkit-autofill': {
      color: '#32325d',
    },
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a',
    ':-webkit-autofill': {
      color: '#fa755a',
    },
  },
};

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  // Elements can use a placeholder as an example IBAN that reflects
  // the IBAN format of your customer's country. If you know your
  // customer's country, we recommend that you pass it to the Element as the
  // placeholderCountry.
  placeholderCountry: 'DE',
  style: IBAN_STYLE,
};

export default function AddSepaModalWrapper(props: AddPaymentModalProps) {
  return (
    <Elements stripe={stripePromise}>
      <AddSepaModal {...props} />
    </Elements>
  );
}

function AddSepaModal(props: AddPaymentModalProps) {
  const { isOpen, toggle, storeId } = props;
  const [sepaMetadata, setSepaMetadata] = useState<any>({});
  const [clientSecret, setClientSecret] = useState<string>('');
  const stripe = useStripe();
  const elements = useElements();

  const [registerSourceIntent, { loading: loadingSourceIntent }] = useMutation(
    registerSourceIntentMutation,
    {
      onCompleted: async (mutationResultData: any) => {
        console.log({ mutationResultData });
        if (mutationResultData.registerSourceIntent) {
          setClientSecret(
            mutationResultData.registerSourceIntent.client_secret,
          );
        }
      },
    },
  );

  const [registerSourceForStore, { loading: updatingSource }] = useMutation(
    registerSourceForStoreMutation,
  );

  useEffect(() => {
    if (!isOpen) return;

    console.log('registerSourceIntent');
    registerSourceIntent({
      variables: {
        user_id: storeId,
        type: paymentMethodTypes.SEPA,
      },
    });
  }, [isOpen]);

  const handleSubmit = async (event: any) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const iban = elements.getElement(IbanElement);

    const result = await stripe.confirmSepaDebitSetup(clientSecret, {
      payment_method: {
        // @ts-ignore
        sepa_debit: iban,
        billing_details: {
          ...sepaMetadata,
          email: `${storeId}@vytal.org`,
        },
      },
    });

    console.log({ result });
    if (result.error) {
      // Show error to your customer.
      alert('Error: ' + result.error.message + '');
    } else {
      await registerSourceForStore({
        variables: {
          store_id: storeId,
          // @ts-ignore
          token: result.setupIntent.payment_method,
          type: paymentMethodTypes.SEPA,
        },
      });
      // close modal
      toggle();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => toggle()}
      className={'show-sepa-modal'}
      size="lg"
    >
      <ModalHeader>{i18n.t('Payment:addsepa_debit')}</ModalHeader>
      <ModalBody>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:name')}
          </span>
          <input
            type="text"
            className="form-control"
            name="iban-name"
            autoComplete="off"
            onChange={(e) =>
              setSepaMetadata({
                ...sepaMetadata,
                name: e.target.value,
              })
            }
            value={sepaMetadata.name || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Payment:iban')}
          </span>
          <StripeIbanInputContainer className="form-control">
            <IbanElement options={IBAN_ELEMENT_OPTIONS} />
          </StripeIbanInputContainer>
        </div>
        <div className="mandate-acceptance">
          By providing your payment information and confirming this payment, you
          authorise (A) VYTAL Global GmbH and Stripe, our payment service
          provider, to send instructions to your bank to debit your account and
          (B) your bank to debit your account in accordance with those
          instructions. As part of your rights, you are entitled to a refund
          from your bank under the terms and conditions of your agreement with
          your bank. A refund must be claimed within 8 weeks starting from the
          date on which your account was debited. Your rights are explained in a
          statement that you can obtain from your bank. You agree to receive
          notifications for future debits up to 2 days before they occur.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary text-white"
          disabled={loadingSourceIntent || !clientSecret || !stripe}
          onClick={handleSubmit}
        >
          {i18n.t('Payment:addsepa_debit')}
        </Button>{' '}
        <Button color="secondary" onClick={() => toggle()}>
          {i18n.t('Common:cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
